import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "The Villas", href: "#villas", testId: "nav-link-villas" },
  { label: "Experiences", href: "#experiences", testId: "nav-link-experiences" },
  { label: "Gallery", href: "#gallery", testId: "nav-link-gallery" },
  { label: "FAQs", href: "#faqs", testId: "nav-link-faqs" },
  { label: "Location", href: "#location", testId: "nav-link-location" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || open;

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        solid ? "border-b border-brand-champagne/70 bg-brand-ivory/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[4.5rem] max-w-7xl items-center justify-between px-5 sm:px-8">
        <div className="flex items-center gap-4">
          <a href="#top" data-testid="nav-logo" className="flex items-center" onClick={() => setOpen(false)}>
            <img
              src={solid ? "/images/dune-villa-black.png" : "/images/dune-villas-white.png"}
              alt="Dune Villas at Area 83"
              className="h-[19px] w-auto sm:h-4"
            />
          </a>
          <Link
            to="/"
            data-testid="nav-parent-brand"
            className={`hidden items-center gap-2 border-l pl-4 text-[11px] uppercase tracking-[0.18em] transition-opacity hover:opacity-60 md:flex ${
              solid ? "border-brand-licorice/20 text-brand-licorice/60" : "border-brand-ivory/30 text-brand-ivory/70"
            }`}
          >
            part of <img src={solid ? "/images/erth-black.png" : "/images/erth-white.png"} alt="erth. escapes" className="h-6 w-auto" />
          </Link>
        </div>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.testId}
              className={`link-underline text-[13px] font-medium uppercase tracking-[0.14em] transition-opacity hover:opacity-60 ${
                solid ? "text-brand-licorice" : "text-brand-ivory"
              }`}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#enquire"
            data-testid="nav-book-cta"
            className="bg-brand-rust px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-brand-ivory transition-colors hover:bg-brand-sepia"
          >
            Book your stay
          </a>
        </nav>

        <button
          type="button"
          data-testid="nav-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className={`lg:hidden ${solid ? "text-brand-licorice" : "text-brand-ivory"}`}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <nav data-testid="nav-mobile-menu" className="border-t border-brand-champagne/70 bg-brand-ivory px-5 pb-8 pt-4 lg:hidden" aria-label="Mobile">
          <ul className="flex flex-col gap-1">
            {LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => setOpen(false)}
                  className="block py-3 font-serif text-2xl text-brand-licorice transition-opacity hover:opacity-60"
                >
                  {l.label}
                </a>
              </li>
            ))}
            <li className="pt-4">
              <a
                href="#enquire"
                data-testid="nav-book-cta-mobile"
                onClick={() => setOpen(false)}
                className="block bg-brand-rust px-6 py-4 text-center text-[13px] font-semibold uppercase tracking-[0.14em] text-brand-ivory"
              >
                Book your stay
              </a>
            </li>
            <li className="pt-6">
              <Link
                to="/"
                data-testid="nav-parent-brand-mobile"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-brand-licorice/60"
              >
                part of <img src="/images/erth-black.png" alt="erth. escapes" className="h-6 w-auto" />
              </Link>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
};
